import React, { useState, useRef, useEffect } from 'react';
import Webcam from 'react-webcam';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';
import WinStateModal from './WinStateModal';
import BeRealCameraCapture from './BeRealCameraCapture';

const RealtimeVerificationModal = ({ isOpen, onClose, duelId, userId, duelTitle, timerMinutes = 2, requireDualCamera = false, onPhotoUploaded }) => {
  const [captureMode, setCaptureMode] = useState('user'); // 'user' or 'environment'
  const [timeLeft, setTimeLeft] = useState(timerMinutes * 60);
  const [timerStarted, setTimerStarted] = useState(false);
  const [captured, setCaptured] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [capturedImages, setCapturedImages] = useState(null); // For dual camera
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [showWinState, setShowWinState] = useState(false);
  const webcamRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (isOpen && !timerStarted) {
      setTimerStarted(true);
      startTimer();
    }

    return () => {
      if (timerRef?.current) {
        clearInterval(timerRef?.current);
      }
    };
  }, [isOpen]);

  const startTimer = () => {
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef?.current);
          handleTimeout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleTimeout = () => {
    setError('¡Tiempo agotado! El reto ha fallado automáticamente.');
    setTimeout(() => {
      onClose();
    }, 3000);
  };

  const handleCapture = () => {
    const imageSrc = webcamRef?.current?.getScreenshot();
    if (imageSrc) {
      setCapturedImage(imageSrc);
      setCaptured(true);
      if (timerRef?.current) {
        clearInterval(timerRef?.current);
      }
    }
  };

  const handleBeRealCapture = (images) => {
    setCapturedImages(images);
    setCaptured(true);
    if (timerRef?.current) {
      clearInterval(timerRef?.current);
    }
  };

  const handleRetake = () => {
    setCaptured(false);
    setCapturedImage(null);
    setError(null);
  };

  const handleUpload = async () => {
    if (requireDualCamera && !capturedImages) return;
    if (!requireDualCamera && !capturedImage) return;

    setUploading(true);
    setError(null);

    try {
      if (requireDualCamera) {
        // Upload both images for BeReal-style verification
        const frontBlob = await (await fetch(capturedImages?.frontImage))?.blob();
        const backBlob = await (await fetch(capturedImages?.backImage))?.blob();
        
        // Upload front camera image - userId must be folder for RLS policy
        const frontFileName = `${userId}/duel_${duelId}_front_${Date.now()}.jpg`;
        const { data: frontUploadData, error: frontUploadError } = await supabase?.storage
          ?.from('duel-proofs')
          ?.upload(frontFileName, frontBlob, {
            contentType: 'image/jpeg',
            upsert: false
          });

        if (frontUploadError) throw frontUploadError;

        // Upload back camera image
        const backFileName = `${userId}/duel_${duelId}_back_${Date.now()}.jpg`;
        const { data: backUploadData, error: backUploadError } = await supabase?.storage
          ?.from('duel-proofs')
          ?.upload(backFileName, backBlob, {
            contentType: 'image/jpeg',
            upsert: false
          });

        if (backUploadError) throw backUploadError;

        // Get public URLs
        const { data: frontUrlData } = supabase?.storage
          ?.from('duel-proofs')
          ?.getPublicUrl(frontFileName);
        
        const { data: backUrlData } = supabase?.storage
          ?.from('duel-proofs')
          ?.getPublicUrl(backFileName);

        // Create duel proof record for front camera
        const { data: frontProofData, error: frontProofError } = await supabase
          ?.from('duel_proofs')
          ?.insert({
            duel_id: duelId,
            user_id: userId,
            photo_url: frontUrlData?.publicUrl,
            proof_type: 'photo',
            proof_value: 1,
            auto_verified: true,
            notes: 'BeReal verification - Front camera'
          })
          ?.select()
          ?.single();

        if (frontProofError) throw frontProofError;

        // Create duel proof record for back camera
        const { data: backProofData, error: backProofError } = await supabase
          ?.from('duel_proofs')
          ?.insert({
            duel_id: duelId,
            user_id: userId,
            photo_url: backUrlData?.publicUrl,
            proof_type: 'photo',
            proof_value: 1,
            auto_verified: true,
            notes: 'BeReal verification - Back camera'
          })
          ?.select()
          ?.single();

        if (backProofError) throw backProofError;

        // Notify parent with both proofs
        onPhotoUploaded?.(frontProofData);
        onPhotoUploaded?.(backProofData);
      } else {
        // Single camera upload
        // Convert base64 to blob
        const response = await fetch(capturedImage);
        const blob = await response?.blob();
        
        // Upload to Supabase Storage - userId must be folder for RLS policy
        const fileName = `${userId}/duel_${duelId}_${Date.now()}.jpg`;
        const { data: uploadData, error: uploadError } = await supabase?.storage
          ?.from('duel-proofs')
          ?.upload(fileName, blob, {
            contentType: 'image/jpeg',
            upsert: false
          });

        if (uploadError) throw uploadError;

        // Get public URL
        const { data: urlData } = supabase?.storage
          ?.from('duel-proofs')
          ?.getPublicUrl(fileName);

        // Create duel proof record (this will trigger real-time notification)
        const { data: proofData, error: proofError } = await supabase
          ?.from('duel_proofs')
          ?.insert({
            duel_id: duelId,
            user_id: userId,
            photo_url: urlData?.publicUrl,
            proof_type: 'photo',
            proof_value: 1,
            auto_verified: true,
            notes: 'Real-time verification upload'
          })
          ?.select()
          ?.single();

        if (proofError) throw proofError;

        // Notify parent component (triggers real-time update via useDuelRealtime hook)
        onPhotoUploaded?.(proofData);
      }

      // Show success state
      setShowWinState(true);
    } catch (error) {
      console.error('Error uploading proof:', error);
      setError(error?.message || 'Error al subir la evidencia');
    } finally {
      setUploading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${String(secs)?.padStart(2, '0')}`;
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
        <div 
          className="absolute inset-0 bg-black/80 backdrop-blur-sm animate-fade-in"
          onClick={onClose}
        />
        
        <div className="relative w-full max-w-2xl bg-card rounded-xl shadow-wellness-lg animate-scale-in elevation-3 overflow-hidden">
          {/* Header with Timer */}
          <div className="flex items-center justify-between p-4 border-b border-border/40 bg-error/10">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-error/20 flex items-center justify-center animate-pulse">
                <Icon name="Clock" size={20} className="text-error" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-foreground">
                  {requireDualCamera ? 'Verificación BeReal' : 'Verificación en Tiempo Real'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {requireDualCamera ? 'Captura con ambas cámaras' : 'Captura tu evidencia ahora'}
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className={`text-3xl font-bold ${
                timeLeft < 30 ? 'text-error animate-pulse' : 'text-warning'
              }`}>
                {formatTime(timeLeft)}
              </div>
              <p className="text-xs text-muted-foreground">Tiempo restante</p>
            </div>
          </div>

          <div className="p-6">
            {!captured ? (
              requireDualCamera ? (
                <BeRealCameraCapture 
                  onCapture={handleBeRealCapture}
                  timeLeft={timeLeft}
                  onTimeout={handleTimeout}
                />
              ) : (
                <div className="space-y-4">
                  {/* Camera View */}
                  <div className="relative rounded-xl overflow-hidden bg-black aspect-video">
                    <Webcam
                      ref={webcamRef}
                      audio={false}
                      screenshotFormat="image/jpeg"
                      videoConstraints={{
                        facingMode: captureMode
                      }}
                      className="w-full h-full object-cover"
                    />
                    
                    {/* Camera Switch Button */}
                    <button
                      onClick={() => setCaptureMode(prev => prev === 'user' ? 'environment' : 'user')}
                      className="absolute top-4 right-4 w-12 h-12 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center hover:bg-black/70 transition-colors"
                    >
                      <Icon name="RefreshCw" size={20} className="text-white" />
                    </button>

                    {/* Timer Overlay */}
                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 px-4 py-2 rounded-full bg-black/70 backdrop-blur-sm">
                      <p className="text-white font-bold text-lg">{formatTime(timeLeft)}</p>
                    </div>
                  </div>

                  {/* Instructions */}
                  <div className="p-4 bg-warning/10 rounded-xl border border-warning/30">
                    <div className="flex items-start space-x-3">
                      <Icon name="AlertCircle" size={20} className="text-warning mt-0.5" />
                      <div className="text-sm text-foreground">
                        <p className="font-semibold mb-1">Instrucciones:</p>
                        <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                          <li>Captura evidencia de que estás completando el reto</li>
                          <li>No puedes seleccionar de galería - solo captura en vivo</li>
                          <li>Si el tiempo se agota, el reto falla automáticamente</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Capture Button */}
                  <Button
                    onClick={handleCapture}
                    size="lg"
                    fullWidth
                    className="bg-error hover:bg-error/90"
                  >
                    <Icon name="Camera" size={20} className="mr-2" />
                    Capturar Foto
                  </Button>
                </div>
              )
            ) : (
              <div className="space-y-4">
                {/* Preview */}
                {requireDualCamera ? (
                  <BeRealCameraCapture 
                    onCapture={handleBeRealCapture}
                    timeLeft={timeLeft}
                    onTimeout={handleTimeout}
                  />
                ) : (
                  <div className="relative rounded-xl overflow-hidden">
                    <img
                      src={capturedImage}
                      alt="Captured proof"
                      className="w-full h-auto"
                    />
                  </div>
                )}

                {/* Actions */}
                <div className="flex space-x-3">
                  {!requireDualCamera && (
                    <Button
                      variant="outline"
                      onClick={handleRetake}
                      disabled={uploading}
                      fullWidth
                    >
                      <Icon name="RotateCcw" size={16} className="mr-1" />
                      Volver a Capturar
                    </Button>
                  )}
                  <Button
                    onClick={handleUpload}
                    loading={uploading}
                    disabled={uploading}
                    fullWidth
                    className="bg-success hover:bg-success/90"
                  >
                    <Icon name="Upload" size={16} className="mr-1" />
                    {uploading ? 'Subiendo...' : 'Subir Evidencia'}
                  </Button>
                </div>

                {error && (
                  <div className="p-3 bg-error/10 border border-error/30 rounded-lg">
                    <div className="flex items-start space-x-2">
                      <Icon name="AlertCircle" size={16} className="text-error mt-0.5" />
                      <p className="text-xs text-error">{error}</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <WinStateModal 
        isOpen={showWinState}
        onClose={() => {
          setShowWinState(false);
          onClose();
        }}
        duelTitle={duelTitle}
      />
    </>
  );
};

export default RealtimeVerificationModal;